<template>
    <ContainerWithModalSide id="app" class="App" name="app-sidebar">
        <transition slot="main" :name="transitionName">
            <router-view :key="routerViewKey" ref="routerView" class="routerView" :ready="true" />
        </transition>

        <DropdownHost class="dropdownHost" name="app-dropdown" />
        <ModalContainer class="modalContainer" name="app-modal" />
        <ModalContainer class="modalContainer" fullscreen name="app-modal-full" />
        <ModalContainer class="modalContainer" name="app-notifications" />

        <DebugButton v-if="showDebugButton" />
        <DebugMenuHost />

        <RouteNavigationBlockingLayer />
    </ContainerWithModalSide>
</template>

<script>
import Vue from 'vue';
import FontFaceObserver from 'fontfaceobserver';

import { mapActions } from 'vuex';

import setupSecretGesture from '@/setup-secret-gesture';
import watchForHover from '@/watch-for-hover';
import { EventBus } from '@/event-bus';
import ContainerWithModalSide from '@/components/ContainerWithModalSide';
import ModalContainer from '@/components/ModalContainer';
import DropdownHost from '@/components/DropdownHost';
import AddToHomescreenPopup from '@/components/AddToHomescreenPopup';

import DebugButton from '@/modules/debug/components/DebugButton';

import LifekeyPopup from '@/modules/dashboard/components/LifekeyPopup';
import ScenariosUnlockedPopup from '@/modules/dashboard/components/ScenariosUnlockedPopup';

import BraindatePopup from '@/modules/dashboard/components/BraindatePopup';
import DailyGoalPopup from '@/modules/dashboard/components/DailyGoalPopup';

import RouteNavigationBlockingLayer from '@/components/RouteNavigationBlockingLayer';
import AppReloadMessage from '@/components/AppReloadMessage';

import SimpleMarkdownViewer from '@/modules/account/components/SimpleMarkdownViewer';
import DebugMenuHost from '@/modules/debug/components/DebugMenuHost';
import whatsnew from '@/whatsnew';

import '@/icons'; // NOTE: makes icons available anywhere

export default {
    name: 'App',
    components: {
        ContainerWithModalSide,
        ModalContainer,
        DropdownHost,
        DebugButton,
        RouteNavigationBlockingLayer,
        DebugMenuHost
    },

    data() {
        return {
            transitionName: '',
            routerViewKey: '',
            defferedPromptEvent: null
        };
    },
    computed: {
        showDebugButton() {
            return this.$store.state.moduleApp.localState.showDebugButton;
        },
        appNeedsReload() {
            return this.$store.getters['moduleApp/appNeedsReload'];
        }
    },
    watch: {
        $route(newRoute, oldRoute) {
            const to = newRoute.matched[0];
            const from = oldRoute.matched[0];

            if (to && from) {
                if (
                    to &&
                    to.meta.foobar &&
                    from &&
                    from.meta.foobar &&
                    newRoute.params.stepId !== oldRoute.params.stepId
                ) {
                    this.transitionName = 'slideUp';
                    this.routerViewKey = newRoute.params.stepId;
                } else if (to.name === 'StepFlowView' && from.name === 'DashboardLifeskillViewWithTab') {
                    this.transitionName = 'fade';
                    this.routerViewKey = '';
                } else if (to.name === 'DashboardLifeskillViewWithTab' && from.name === 'StepFlowView') {
                    this.transitionName = 'fade';
                    this.routerViewKey = '';
                } else {
                    this.transitionName = '';
                    this.routerViewKey = '';
                }
            } else {
                this.transitionName = '';
                this.routerViewKey = '';
            }
        },
        appNeedsReload: {
            handler(showAppReloadMessage) {
                if (document.body.querySelector('#appReloadMessage')) {
                    document.body.classList.remove('showingAppReloadMessage');
                    if (this.appReloadMessage) {
                        this.appReloadMessage.$destroy();
                        this.appReloadMessage = null;
                    }
                    document.body.removeChild(document.body.querySelector('#appReloadMessage'));
                }
                if (showAppReloadMessage) {
                    document.body.classList.add('showingAppReloadMessage');
                    document.body.insertAdjacentHTML('afterBegin', '<div id="appReloadMessage"></div>');

                    /* eslint-disable no-new */
                    this.appReloadMessage = new Vue({
                        el: '#appReloadMessage',
                        i18n: this.$i18n,
                        render: h => h(AppReloadMessage)
                    });
                }
            },
            immediate: true
        }
    },
    async mounted() {
        await this.loadSystems();

        const font = new FontFaceObserver('roboto');
        font.load().then(() => {
            // NOTE: for some strange reason, the font is ready sooner when this block is run...
        });
        setupSecretGesture(() => {
            this.$store.dispatch('moduleApp/toggleDebugButton');
        });
        watchForHover();

        this.$store.watch(
            state => this.$store.state.initialUserStateLoaded,
            ready => {
                if (ready) {
                    // Only show message if it has not been shown before:
                    if (
                        whatsnew.currentId &&
                        this.$store.state.moduleApp.memolife.whatsNewViewedArticles.indexOf(whatsnew.currentId) === -1
                    ) {
                        // Only show message if user created before message created:
                        const timestamp = whatsnew.meta[whatsnew.currentId].timestamp;
                        if (this.$store.getters['moduleAuth/profile'].created_at * 1000 < timestamp) {
                            this.showWhatsNew();
                        }
                    }
                }
            },
            { immediate: true }
        );

        EventBus.$on('new-lifekey', ({ continueToDashboard }) => {
            const belt = this.$store.getters['moduleApp/belt'];
            setTimeout(() => {
                this.$modal.open({
                    component: LifekeyPopup,
                    props: {
                        belt,
                        continueToDashboard
                    },
                    target: 'app-modal'
                });
            }, 1000);
        });
        EventBus.$on('newBraindate', braindate => {
            if (braindate.isBonus) {
                return;
            }
            const requiredDailyBraindates = this.$store.state.moduleApp.memolife.dailyRequiredBraindates;
            const braindateCountToday = this.$store.getters['moduleApp/braindatesForCurrentDay'].length;
            const justReachedGoal = braindateCountToday === requiredDailyBraindates;

            this.$modal.open({
                target: 'app-notifications',
                channelId: 'notifications',
                wrap: true,
                props: {
                    lifeskillId: braindate.lifeskillNumber,
                    goal: requiredDailyBraindates,
                    count: braindateCountToday,
                    extraLifeskillIds: braindate.bonusBraindates,
                    horizontalLayout: window.innerWidth >= 480 // NOTE: mobile breakpoint
                },
                hostOptions: { alignment: 'top-right', maskColor: 'transparent' },
                component: BraindatePopup
            });

            if (justReachedGoal) {
                this.$modal.open({
                    target: 'app-notifications',
                    channelId: 'notifications',
                    wrap: true,
                    props: {
                        lifeskillId: braindate.lifeskillNumber,
                        useAsBraindatePopup: true,
                        goal: requiredDailyBraindates,
                        count: braindateCountToday
                    },
                    component: DailyGoalPopup
                });
            }
        });

        EventBus.$on('showWhatsNew', () => {
            this.showWhatsNew();
        });

        EventBus.$on('scenarios-unlocked', lifeskillId => {
            this.$modal.open({
                component: ScenariosUnlockedPopup,
                props: {
                    lifeskillId
                },
                target: 'app-modal'
            });
        });
    },
    created() {
        window.addEventListener('beforeinstallprompt', e => {
            e.preventDefault();

            const userAgent = navigator.userAgent || navigator.vendor || window.opera;

            const isAndroid = /android/i.test(userAgent);

            if (isAndroid) {
                this.defferedPromptEvent = e;
            }
        });
    },
    methods: {
        ...mapActions({ loadSystems: 'moduleCourse/loadSystems' }),
        showWhatsNew() {
            if (!whatsnew.currentId) {
                return; // NOTE: no support for showing older news ATM
            }
            const lang = this.$store.getters['moduleApp/locale'];

            const meta = whatsnew.meta[whatsnew.currentId];
            if (meta.languages.indexOf(lang) === -1) {
                return;
            }
            this.$store.dispatch('moduleApp/updateWhatsNewViewedArticles', { id: whatsnew.currentId });

            this.$modal.open({
                target: 'app-modal',
                wrap: true,
                component: SimpleMarkdownViewer,
                props: {
                    url: `whats-new/${whatsnew.currentId}-${lang}.md`,
                    content: 'some **bla bla** content',
                    markdown: true
                }
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.App {
    color: #2c3e50;
    background: #143979;
    height: 100%;
    .slide-leave-active,
    .slide-enter-active {
        transition: 1s;
        top: 10px;
    }
    .slide-enter {
        top: -100px;
    }
    .slide-leave-to {
        top: -100px;
    }
}
.routerView {
    height: 100%;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;

    $transitionDuration: 0.5s;
    &.slideUp-enter-active,
    &.slideUp-leave-active {
        transition: transform $transitionDuration ease-out, opacity $transitionDuration;
    }
    &.slideUp-enter {
        transform: translate3d(0, 100%, 0);
    }
    &.slideUp-enter-to {
        transform: translate3d(0%, 0, 0);
    }
    &.slideUp-leave-to {
        transform: translate3d(0, -100%, 0);
    }

    &.fade-enter-active,
    &.fade-leave-active {
        transition: transform $transitionDuration ease-out, opacity $transitionDuration;
    }
    &.fade-enter {
        opacity: 0;
    }

    &.fade-leave-to {
        opacity: 0;
    }
}

.modalContainer {
    z-index: 3;
}

.dropdownHost {
    z-index: 1;
}
</style>
